import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, setToken, apiErr } from "@/lib/api";
import { Logo, Button, Field, Input, Select } from "@/lib/ui";
import { useAuth } from "@/context/Auth";

export default function SellerAuth() {
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [type, setType] = useState("retailer");
  const [referredBy, setReferredBy] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [acks, setAcks] = useState([false, false, false]);
  const [stockValue, setStockValue] = useState(null);
  const [visits, setVisits] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => { setStockValue(data.stock_sold_value); setVisits(data.marketplace_visits); }).catch(() => {});
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("ds_remember_seller");
    if (saved) { setPhone(saved); setRemember(true); }
  }, []);

  const afterAuth = (data) => {
    setToken(data.token);
    setUser(data.user);
    if (data.must_change_password) nav("/seller/change-password");
    else if (!data.user.profile_complete) nav("/seller/onboard");
    else nav("/seller/app");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "register") {
        if (!acks.every(Boolean)) { toast.error("Please accept all the policies to proceed"); setLoading(false); return; }
        const { data } = await api.post("/auth/seller/register", { phone, seller_type: type, referred_by: referredBy || undefined });
        toast.success("Registered! Your temporary password is your mobile number.");
        afterAuth(data);
      } else {
        const { data } = await api.post("/auth/seller/login", { phone, password });
        if (remember) localStorage.setItem("ds_remember_seller", phone);
        else localStorage.removeItem("ds_remember_seller");
        afterAuth(data);
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-4 py-10">
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white p-8 fade-up">
        <Logo />
        {stockValue > 0 && (
          <div className="mt-5 flex justify-center" data-testid="stock-sold-stamp">
            <div className="relative -rotate-6 rounded-2xl border-4 border-double border-emerald-600 bg-emerald-50 px-6 py-3 text-center shadow-md">
              <i className="fa-solid fa-certificate absolute -right-2 -top-2 text-emerald-500" />
              <div className="font-head text-3xl font-extrabold leading-none text-emerald-700" data-testid="stock-sold-count">₹{stockValue.toLocaleString("en-IN")}/-</div>
              <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">Worth Of Stock Sold Till Date</div>
            </div>
          </div>
        )}
        {visits > 0 && (
          <p className="mt-3 flex items-center justify-center gap-2 text-xs font-semibold text-slate-500" data-testid="signin-visits">
            <i className="fa-solid fa-users text-orange-500" /> {visits.toLocaleString("en-IN")} people visited the marketplace till date
          </p>
        )}
        <div className="mt-6 flex rounded-lg bg-slate-100 p-1 text-sm font-semibold">
          <button data-testid="seller-tab-login" onClick={() => setMode("login")} className={`flex-1 rounded-md py-2 ${mode === "login" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Login</button>
          <button data-testid="seller-tab-register" onClick={() => setMode("register")} className={`flex-1 rounded-md py-2 ${mode === "register" ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>Register</button>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <Field label="Mobile Number" required>
            <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3">
              <span className="text-sm font-semibold text-slate-500">+91</span>
              <input
                data-testid="seller-phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                placeholder="Phone Number"
                className="w-full border-0 py-2.5 text-sm outline-none"
                inputMode="numeric"
              />
            </div>
          </Field>

          {mode === "register" ? (
            <>
              <Field label="Seller Type" required>
                <Select data-testid="seller-type-select" value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="retailer">Retailer</option>
                  <option value="wholesaler">Wholesaler</option>
                  <option value="manufacturer">Manufacturer</option>
                </Select>
              </Field>
              <Field label="Referred By (optional)" hint="If a seller referred you, enter their 10-digit mobile number.">
                <div className="flex items-center gap-2 rounded-lg border border-slate-300 px-3">
                  <span className="text-sm font-semibold text-slate-500">+91</span>
                  <input
                    data-testid="seller-referred-by-input"
                    value={referredBy}
                    onChange={(e) => setReferredBy(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="Referrer's mobile (optional)"
                    className="w-full border-0 py-2.5 text-sm outline-none"
                    inputMode="numeric"
                  />
                </div>
              </Field>
            </>
          ) : (
            <>
              <Field label="Password" required hint="First-time login? Use your mobile number as the password.">
                <div className="relative">
                  <Input data-testid="seller-password-input" type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="pr-10" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} data-testid="seller-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><i className={`fa-solid ${showPw ? "fa-eye-slash" : "fa-eye"}`} /></button>
                </div>
              </Field>
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" data-testid="seller-remember" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="h-4 w-4 accent-orange-600" />
                Remember me (stay signed in on this device)
              </label>
            </>
          )}

          {mode === "register" && (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="seller-acks">
              {[
                "I understand that deadstock.live scope is to just connect buyer and seller at the website www.deadstock.live",
                "I accept that no refund will be made by www.deadstock.live on any grounds.",
                "I accept the above policies of the company and wish to proceed further",
              ].map((t, i) => (
                <label key={i} className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
                  <input type="checkbox" data-testid={`seller-ack-${i + 1}`} checked={acks[i]}
                    onChange={(e) => setAcks((a) => { const n = [...a]; n[i] = e.target.checked; return n; })}
                    className="mt-0.5 h-4 w-4 flex-shrink-0 accent-orange-600" />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          )}

          <Button data-testid="seller-submit-btn" type="submit" disabled={loading || (mode === "register" && !acks.every(Boolean))} className="w-full">
            {loading ? "Please wait…" : mode === "register" ? "Create Seller Account" : "Login"}
          </Button>
        </form>

        {mode === "register" && (
          <p className="mt-4 rounded-lg bg-orange-50 px-3 py-2 text-xs text-orange-700">
            Your mobile number becomes your temporary password. Choose an annual package after registering to start listing.
          </p>
        )}
        <button onClick={() => nav("/referor")} data-testid="referor-option-btn"
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 px-4 py-3 text-sm font-bold text-slate-800 transition hover:border-orange-400 hover:bg-orange-50">
          <i className="fa-solid fa-user-tie text-orange-600" /> Referor
        </button>
        <button onClick={() => nav("/")} className="mt-4 text-xs font-semibold text-slate-400 hover:text-slate-600">
          <i className="fa-solid fa-chevron-left mr-1" /> Back
        </button>
      </div>
    </div>
  );
}
